import { createFileRoute, Link } from '@tanstack/react-router';
import { productIdPathParams } from '@/lib/product-url-codec';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { formatDuration, formatServerDateShort, parseServerDate } from '@/lib/dates';
import { useMe } from '@/features/auth/hooks';
import { useStrategyRun, useStrategyDispatchDetail } from '@/features/strategy/hooks';
import type { StrategyRunDetail } from '@/features/strategy/api';
import { StrategyResultRenderer } from '@/features/strategy/components/StrategyResultRenderer';
import { StrategyDispatchForm } from '@/features/strategy/components/StrategyDispatchForm';
import { StaleReportBanner } from '@/features/strategy/components/StaleReportBanner';
import { useProducts } from '@/features/product/hooks';
import { PageHero } from '@/components/layout/PageHero';
import { DispatchTabsShell } from '@/features/agent-dispatch/DispatchTabsShell';
import { QueuedBanner } from '@/features/agent-dispatch/QueuedBanner';
import { FailedBanner as DispatchFailedBanner } from '@/features/agent-dispatch/FailedBanner';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import type { DispatchData } from '@/features/agent-dispatch/types';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Regulatory Strategy result page.
 *
 * URL: /regulatory-strategy/:productId?strategy_run_id=<n>
 *
 * Polls the strategy run every poll_ms (server-tuned, ~3s default). Shows:
 *   - Run-identity strip (id · started · status pill)
 *   - In-progress workflow stepper (6 strategy-specific steps × 12s)
 *   - Failed banner with friendly error copy
 *   - Completed banner + StrategyResultRenderer + result disclaimer
 *
 * Polling stops automatically once a terminal state is reached.
 */

const searchSchema = z.object({
  strategy_run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const STRATEGY_WORKFLOW_STEPS = [
  'Read product specifications',
  'Query regulatory databases',
  'Identify regulatory pathway',
  'Evaluate predicate landscape',
  'Draft strategy roadmap',
  'Finalize recommendations',
] as const;

const STRATEGY_STEP_SECONDS = 12;

export const Route = createFileRoute('/regulatory-strategy_/$productId')({
  // Opaque token in the address bar, never the global product id.
  params: productIdPathParams,
  validateSearch: searchSchema,
  component: RegulatoryStrategyResultPage,
});

function RegulatoryStrategyResultPage() {
  const { productId } = Route.useParams();
  const { strategy_run_id: runId } = Route.useSearch();
  const { data: run, isLoading, isError } = useStrategyRun(runId ?? null);

  // A run id pointing at ANOTHER product's run is as unusable as a
  // non-numeric one — degrade it exactly the way the search schema's
  // `.catch(undefined)` degrades garbage: render this product's own default
  // view instead of a foreign report. Without this, a hand-edited or spliced
  // URL renders product A's breadcrumb around product B's full report (and
  // its Download PDF). Numeric compare on both sides: the route param is a
  // string and may carry leading zeros.
  const runBelongsHere =
    run == null || Number(run.product_id) === Number(productId);

  return (
    <>
      <PageHero
        title="Regulatory Strategy"
        subtitle="A full regulatory roadmap for this product — pathway recommendation, evidence requirements, timeline, and risk register — generated per target market by the REGARA AI agent."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-7 pb-20">
        {(runId === undefined || (run && !runBelongsHere)) && (
          <DispatchOrMissing productId={productId} />
        )}
        {runId !== undefined && isLoading && (
          <RunLoadingSkeleton />
        )}
        {runId !== undefined && isError && (
          <ErrorCard message="Could not load the run. Please refresh." />
        )}
        {runId !== undefined && run && runBelongsHere && (
          run.dispatch_group_id ? (
            <DispatchView groupId={run.dispatch_group_id} initialRunId={run.id} />
          ) : (
            <>
              <AgentRunCard run={run} agentLabel="Strategy run" />
              <RunStateBody run={run} />
              {run.status === 'completed' && <ResultDisclaimer />}
            </>
          )
        )}
      </main>
      <ScrollToTopButton />
    </>
  );
}

function RunStateBody({ run, country }: { run: StrategyRunDetail; country?: string | null }) {
  // Gate the leave-the-page promise on the backend's effective email state.
  const me = useMe();
  if (run.status === 'queued') {
    return <QueuedBanner country={country} />;
  }
  if (run.status === 'running') {
    return (
      <>
      <AgentPollingPanel
        startedAt={run.started_at}
        workflowSteps={STRATEGY_WORKFLOW_STEPS}
        stepSeconds={STRATEGY_STEP_SECONDS}
        title={
          country ? (
            <>
              Strategy in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          ) : (
            'Strategy in Progress'
          )
        }
        subtitle="Querying regulatory databases via the REGARA AI agent to build the pathway and submission roadmap."
      />
      {me.data?.run_email_enabled ? (
        <p className="text-[12.5px] text-muted text-center mt-3">
          You can leave this page we&apos;ll email you when the report is ready.
        </p>
      ) : me.data?.run_email_admin_enabled && me.data.agent_email_prefs?.strategy === false ? (
        <p className="text-[12.5px] text-muted text-center mt-3">
          Email notifications for Regulatory Strategy are off —{' '}
          <Link to="/profile" className="underline">turn them on in Profile &amp; Settings</Link>.
        </p>
      ) : null}
      </>
    );
  }

  if (run.status === 'failed') {
    return (
      <DispatchFailedBanner
        country={country}
        message={friendlyErrorMessage(run.error_message)}
      />
    );
  }

  return <CompletedSection run={run} country={country} />;
}

/** Multi-country view. Polls every sibling run in the dispatch group and
 *  renders one tab per country. When the dispatch has a single child
 *  (today's single-country case), the shell auto-collapses to the legacy
 *  inline view. Mirrors the dossier-side ``DispatchView`` exactly. */
function DispatchView({
  groupId,
  initialRunId,
}: {
  groupId: string;
  initialRunId?: number | null;
}) {
  const { data, isLoading, isError } = useStrategyDispatchDetail(groupId);
  const shellData: DispatchData | undefined = data
    ? { dispatch_group_id: data.dispatch_group_id, runs: data.runs }
    : undefined;
  return (
    <DispatchTabsShell
      data={shellData}
      isLoading={isLoading}
      isError={isError}
      initialRunId={initialRunId}
      renderRunMeta={(runId) => <StrategyRunMetaCard runId={runId} />}
      renderSingleRun={(runId) => <StrategySingleRun runId={runId} />}
      renderRunBody={(runId, country) => (
        <StrategyRunBody runId={runId} country={country} />
      )}
    />
  );
}

function StrategyRunMetaCard({ runId }: { runId: number }) {
  const { data: run } = useStrategyRun(runId);
  if (!run) return null;
  return <AgentRunCard run={run} agentLabel="Strategy run" />;
}

function StrategySingleRun({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useStrategyRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel="Strategy run" />
      <RunStateBody run={run} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}

function StrategyRunBody({
  runId,
  country,
}: {
  runId: number;
  country: string | null;
}) {
  const { data: run, isLoading, isError } = useStrategyRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <RunStateBody run={run} country={country} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}

function CompletedSection({
  run,
  country,
}: {
  run: StrategyRunDetail;
  country?: string | null;
}) {
  const startedAt = parseServerDate(run.started_at);
  const completedAt = parseServerDate(run.completed_at);
  const durationSec =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  const response = run.strategy_response;

  return (
    <>
      <StaleReportBanner
        productId={Number(run.product_id)}
        runId={Number(run.id)}
        runCompletedAt={run.completed_at}
        country={run.country}
      />
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              Strategy Complete
              {country && (
                <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                  {countryLabel(country)}
                </span>
              )}
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {formatServerDateShort(run.completed_at)}
              {durationSec !== null && ` · ${formatDuration(durationSec)}`}
            </p>
          </div>
        </div>
      </div>

      {response ? (
        <StrategyResultRenderer
          response={response}
          runId={run.id}
          pathway={run.pathway}
          downloadFormat={run.download_format}
        />
      ) : (
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
          <div className="font-semibold text-state-error-text text-lg">
            Could not parse the agent response
          </div>
          <p className="text-sm text-state-error-text mt-1">
            The run completed but the strategy payload was not delivered. Try generating
            a new strategy.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * Renders the dispatch form (country picker + Generate) when we can
 * resolve the product. Falls back to a friendly "product not found"
 * card when ``useProducts()`` doesn't return the requested id. Same
 * shape as clinical-protocol's DispatchOrMissing branch.
 */
function DispatchOrMissing({ productId }: { productId: string }) {
  const { data: products, isLoading } = useProducts();
  if (isLoading) {
    return <RunLoadingSkeleton label="Loading product" />;
  }
  const numericId = Number(productId);
  const product = products?.find((p) => p.id === numericId) ?? null;
  if (!product) {
    return (
      <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-8 text-state-warning-text">
        <div className="font-semibold text-lg">Product not found</div>
        <p className="text-sm mt-1">
          This product either doesn&rsquo;t exist or
          isn&rsquo;t accessible to your account. Return to the list to
          pick a different product.
        </p>
        <Button variant="outline" asChild className="mt-4">
          <a href="/regulatory-strategy">← Back to product list</a>
        </Button>
      </div>
    );
  }
  return <StrategyDispatchForm product={product} />;
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
      {message}
    </div>
  );
}

function ResultDisclaimer() {
  return (
    <footer className="mt-7 py-[18px] px-[22px] bg-state-warning-bg-soft border border-state-warning-border rounded-xl text-[#713f12] text-base leading-[1.6]">
      <strong className="block text-xs font-bold uppercase tracking-[0.22em] text-state-warning-text mb-1.5">
        Disclaimer.
      </strong>
      This is REGARA AI-generated decision-support, not legal or regulatory advice.
      Regulatory decisions rest with the relevant authority. Review every strategy
      recommendation with a qualified
      regulatory professional before relying on it.
    </footer>
  );
}

function friendlyErrorMessage(raw: string | null): string {
  const msg = (raw ?? '').toString();
  if (!msg) {
    return 'The strategy generation did not complete. Try running it again from the product list.';
  }
  if (/no JSON|did not produce valid/i.test(msg)) {
    return 'The agent did not produce a valid strategy. Try starting a new run.';
  }
  if (/timeout|timed out/i.test(msg)) {
    return 'The strategy generation took too long to complete. Try starting a new run.';
  }
  if (/auth|api[_ ]?key|unauthor/i.test(msg)) {
    return 'The agent could not reach the strategy service. Please contact support.';
  }
  return 'Something went wrong while generating the strategy. Try starting a new run, or contact support if this keeps happening.';
}
